import type { ReactNode } from 'react'
import React, { createContext, useCallback, useMemo } from 'react'

import type { PaginationProps, TableHeaders, TableProps } from '@fourthwall/components'
import {
  ConfirmModal,
  Dropdown,
  ToolbarButton,
  useMediaQueryContext,
  useModalContext,
  usePagination,
  useToastsContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon, PencilIcon, TrashIcon } from '@fourthwall/icons'
import { pluralize } from '@fourthwall/utils/dist/string'

import { TagCell } from '../../components/NameCell'
import { useCreatePostTagMutation } from '../../hooks/useCreatePostTagMutation'
import { useDeletePostTagMutation } from '../../hooks/useDeletePostTagMutation'
import { usePostTagsQuery } from '../../hooks/usePostTagsQuery'
import { useUpdatePostTagMutation } from '../../hooks/useUpdatePostTagMutation'
import { PostTagModal } from '../../modules/PostTagModal/PostTagModal'
import type { PostTag, TableData } from '../../types'

type Props = { children: ReactNode }

type PostTagsContextType = {
  badges?: PostTag[]
  table: TableProps
  pagination: PaginationProps
  openPostTagModal: () => void
  isFetching: boolean
  isLoading: boolean
}

const POST_TAGS_LIMIT = 50

export const PostTagsContext = createContext<PostTagsContextType | undefined>(undefined)

export const PostTagsProvider = ({ children }: Props) => {
  const [currentPage, setCurrentPage] = usePagination()

  const modal = useModalContext()
  const toast = useToastsContext()
  const media = useMediaQueryContext()

  const { postTagsQuery } = usePostTagsQuery([
    {
      limit: POST_TAGS_LIMIT,
      page: currentPage,
    },
  ])

  const { createPostTagMutation } = useCreatePostTagMutation()
  const { updatePostTagMutation } = useUpdatePostTagMutation()
  const { deletePostTagMutation } = useDeletePostTagMutation()

  const deletePostTag = useCallback(
    (badgeId: number) => {
      modal.open(ConfirmModal, {
        title: 'Are you sure you want to delete this tag?',
        confirmLabel: 'Delete tag',
        confirmAppearance: 'destructive',
        onConfirm: () => {
          deletePostTagMutation.mutate([{ id: badgeId }], {
            onSuccess: postTagsQuery.refetch,
          })
        },
      })
    },
    [postTagsQuery, deletePostTagMutation, modal],
  )

  const openEditPostTagModal = useCallback(
    (tag: PostTag) => {
      modal.open(PostTagModal, {
        tag,
        onSubmit: (values) => {
          return updatePostTagMutation.mutateAsync(
            [
              { id: tag.id },
              {
                name: values.text,
              },
            ],
            {
              onSuccess: () => {
                postTagsQuery.refetch()
                modal.close()
              },
            },
          )
        },
        onClose: modal.close,
      })
    },
    [modal, postTagsQuery, updatePostTagMutation],
  )

  const openPostTagModal = useCallback(() => {
    modal.open(PostTagModal, {
      onSubmit: (values) => {
        return createPostTagMutation.mutateAsync(
          [
            {
              name: values.text,
            },
          ],
          {
            onSuccess: () => {
              toast.notify('Tag created successfully', { type: 'success' })
              postTagsQuery.refetch()
              modal.close()
            },
          },
        )
      },
      onClose: modal.close,
    })
  }, [modal, postTagsQuery, createPostTagMutation, toast])

  const tableHeaders: TableHeaders<TableData> = useMemo(
    () => [
      {
        id: 'tag',
        label: 'Tag',
        width: 2,
        render: (tag) => <TagCell>{tag.name}</TagCell>,
      },
      {
        id: 'assignedTo',
        label: 'Assigned to',
        width: 2,
        render: (tag) => `${tag.postsCount} ${pluralize(tag.postsCount, 'post')}`,
      },
      {
        id: 'settings',
        label: ' ',
        alignment: 'right',
        width: 1,
        render: (tag) => (
          <Dropdown
            content={
              <>
                <Dropdown.Item
                  label="Edit"
                  leftIcon={PencilIcon}
                  onClick={() => {
                    openEditPostTagModal(tag)
                  }}
                />
                <Dropdown.Item
                  label="Delete"
                  leftIcon={TrashIcon}
                  onClick={() => {
                    deletePostTag(tag.id)
                  }}
                />
              </>
            }
            position="bottom-end"
          >
            <ToolbarButton icon={DotsHorizontalIcon} />
          </Dropdown>
        ),
      },
    ],
    [deletePostTag, openEditPostTagModal],
  )

  const mobileTableHeaders: TableHeaders<TableData> = useMemo(
    () => [
      {
        id: 'tag',
        label: 'Tag',
        render: (tag) => (
          <TagCell info={`Assigned to ${tag.postsCount} ${pluralize(tag.postsCount, 'post')}`}>
            {tag.name}
          </TagCell>
        ),
        width: 2,
      },
      {
        id: 'settings',
        label: ' ',
        alignment: 'right',
        width: 1,
        render: (tag) => (
          <Dropdown
            content={
              <>
                <Dropdown.Item
                  label="Edit"
                  leftIcon={PencilIcon}
                  onClick={() => {
                    openEditPostTagModal(tag)
                  }}
                />
                <Dropdown.Item
                  label="Delete"
                  leftIcon={TrashIcon}
                  onClick={() => {
                    deletePostTag(tag.id)
                  }}
                />
              </>
            }
            position="bottom-end"
          >
            <ToolbarButton icon={DotsHorizontalIcon} />
          </Dropdown>
        ),
      },
    ],
    [openEditPostTagModal, deletePostTag],
  )

  const tableData: TableData[] = useMemo(() => {
    if (!postTagsQuery.data) return []

    return postTagsQuery.data.tags
  }, [postTagsQuery.data])

  const table: TableProps<TableData> = useMemo(
    () => ({
      data: tableData,
      headers: media.tablet ? tableHeaders : mobileTableHeaders,
      emptyMessage: 'You haven’t created any psot tag yet.',
      minWidth: 0,
    }),
    [tableData, media, tableHeaders, mobileTableHeaders],
  )

  const pagination: PaginationProps = useMemo(
    () => ({
      pageCount: postTagsQuery.data?.pagination?.pages || 1,
      currentPage,
      total: postTagsQuery.data?.pagination?.total
        ? {
            count: postTagsQuery.data.pagination.total,
            limit: POST_TAGS_LIMIT,
          }
        : undefined,
      onChange: setCurrentPage,
    }),
    [postTagsQuery.data, currentPage, setCurrentPage],
  )

  const value = useMemo(
    () => ({
      tags: postTagsQuery.data?.tags,
      table,
      pagination,
      openPostTagModal,
      isFetching: !postTagsQuery.data?.tags,
      isLoading: postTagsQuery.isFetching || deletePostTagMutation.isLoading,
    }),
    [postTagsQuery, table, pagination, openPostTagModal, deletePostTagMutation.isLoading],
  )

  return <PostTagsContext.Provider value={value}>{children}</PostTagsContext.Provider>
}
